/// <reference types="dom-mediacapture-record" />

import { Injectable, OnDestroy } from '@angular/core';
import { NoraAPIService } from '../services/nora-api.service';
import { BehaviorSubject, combineLatest, EMPTY, from, interval, merge, Observable, Subject, of } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import {
  catchError,
  delay,
  distinctUntilChanged,
  exhaustMap,
  filter,
  finalize,
  map,
  mergeMap,
  shareReplay,
  switchMap,
  take,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { devLog, logError } from '@app/core/functions/tap-log';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { AuthService, NavigationService, UiStateService } from '@app/core/services';
import { LogoutConfirmationDialogComponent } from '@app/core/nav/logout-confirmation-dialog/logout-confirmation-dialog.component';
import { SidebarLocations } from '@app/core/services/state/ui-state/ui-state.store';
import { ClinicalEncounterService } from '@app/core/services/clinical-encounter.service';
import { EncounterStatusVo } from '@app/core/services/api-client.service';
import { NoraManagementService } from './nora-management.service';
import { Language } from './nora-management.service';
import { quillDeltaToClinicalNoteHtml } from '@app/shared/functions/clinical-note';
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
  defaultDialogActions,
  DialogResponse,
} from '@app/shared/components/confirm-dialog/confirm-dialog.component';

export type RecordingStatus = 'idle' | 'recording' | 'paused';
export type ConcludeRecordingResult = 'concluded' | 'cancelled';

type ConcludedEncounterContext = {
  encounter: EncounterStatusVo;
  practiceId: string;
  encounterId: string;
  practiceTenantId: string;
  patientId: string;
};

// Snapshot of data as class is singleton so cant reliably reference
// this.practiceid/encounterid
type RecordingSnapshot = {
  practiceId: string;
  encounterId: string;
  micUsed: string;
  recordingDuration: number;
  dictationMode: string;
  language: Language;
};

@Injectable({
  providedIn: 'root',
})
export class AudioRecorderService implements OnDestroy {
  practiceId: string | null = null;
  encounterId: string | null = null;
  practiceTenantId: string | null = null;

  micUsed: string | null = null;
  recordingDuration: number = 0;
  dictationMode: string = '';
  durationInterval: any = null;

  language: Language = 'english';

  mediaRecorder!: MediaRecorder;
  audioChunks: Blob[] = [];
  mediaStream!: MediaStream;
  file: File | null = null;

  params$ = combineLatest([this.authService.currentTenantId$, this.uiStateService.currentPatientId$]).pipe(
    map(([tenantId, patientId]) => ({ tenantId, patientId })),
    distinctUntilChanged()
  );

  encounter$ = this.params$.pipe(
    switchMap(params => this.clinicalEncounterService.encounterInProgress$(params.tenantId, params.patientId)),
    untilDestroyed(this),
    shareReplay(1)
  );

  private handleAudioDataSubject = new Subject<{ blob: Blob; snapshot: RecordingSnapshot }>();

  // mergeMap (not switchMap): concluding patient A must never cancel a
  // still-running poll for patient B.
  private concludeRequestedSubject = new Subject<void>();

  private recordingSubject = new BehaviorSubject<boolean>(false);
  recording$ = this.recordingSubject.asObservable();

  private pausedSubject = new BehaviorSubject<boolean>(false);
  paused$ = this.pausedSubject.asObservable();

  private isProcessingNotesSubject = new BehaviorSubject<boolean>(false);
  isProcessingNotes$ = this.isProcessingNotesSubject.asObservable();

  // Add a new subject to track component visibility
  private componentVisibleSubject = new BehaviorSubject<boolean>(true);
  componentVisible$ = this.componentVisibleSubject.asObservable();

  // Add new subjects for UI state
  private recordingDurationSubject = new BehaviorSubject<number>(0);
  recordingDuration$ = this.recordingDurationSubject.asObservable();

  private durationIntervalSubject = new BehaviorSubject<any>(null);
  durationInterval$ = this.durationIntervalSubject.asObservable();

  private patientIdSubject = new BehaviorSubject<string>('');
  patientId$ = this.patientIdSubject.asObservable();

  private selectedDeviceSubject = new BehaviorSubject<{ id: string; label: string }>({ id: '', label: '' });
  selectedDevice$ = this.selectedDeviceSubject.asObservable();

  private showRecordingPlanLimitReachedSubject = new BehaviorSubject<boolean>(false);
  showRecordingPlanLimitReached$ = this.showRecordingPlanLimitReachedSubject.asObservable();

  private stepperStageSubject = new BehaviorSubject<any>(null);
  stepperStage$ = this.stepperStageSubject.asObservable();

  private generationProcessingSubject = new BehaviorSubject<boolean>(false);
  generationProcessing$ = this.generationProcessingSubject.asObservable();

  // Fires once per individual recording that has been finalized and queued
  // for upload. Consumers (e.g. HBC Go usage limits) use this to count
  // discrete recordings rather than one count per consultation.
  private recordingSubmittedSubject = new Subject<void>();
  recordingSubmitted$ = this.recordingSubmittedSubject.asObservable();

  private statusCache = new Map<string, Observable<RecordingStatus>>();

  // Keyed by encounterId: a second concludeRecording$/uploadAudio call for the
  // same encounter joins this poll instead of duplicating it.
  private activePolls = new Map<string, Observable<any>>();

  constructor(
    private noraAPIService: NoraAPIService,
    private noraManagementService: NoraManagementService,
    private snackbar: MatSnackBar,
    private authService: AuthService,
    private clinicalEncounterService: ClinicalEncounterService,
    private uiStateService: UiStateService,
    private navService: NavigationService
  ) {
    this.handleAudioDataSubject.pipe(tap(({ blob, snapshot }) => this.handleAudioData(blob, snapshot))).subscribe();

    // Permanent, not tied to any component, so stop/upload/poll survives
    // navigation; catchError stops one failure from killing this for good.
    this.concludeRequestedSubject.pipe(mergeMap(() => this.concludeRecording$().pipe(catchError(() => of(undefined))))).subscribe();

    // Monitor duration and subscription limits
    this.recordingDuration$
      .pipe(
        withLatestFrom(this.noraManagementService.getSubscriptionLevel()),
        tap(([durationSec, subscriptionLevel]) => {
          if ((subscriptionLevel === 'Premium' || subscriptionLevel === 'FreeTrial') && durationSec === 25 * 60) {
            this.snackbar.open('Please note that you only have 5min left to record.', 'Close', { duration: 5000 });
          }

          if (
            subscriptionLevel in this.noraManagementService.limits &&
            durationSec >= this.noraManagementService.limits[subscriptionLevel]
          ) {
            const message =
              subscriptionLevel === 'FreeTrial'
                ? 'Your recording has been paused. Each recording on a Free Trial is limited to 30 minutes. Upgrade to Pro subscription for longer recording time.'
                : 'Please note that the recording has been stopped as you have now reached the maximum allowable minutes to record';

            this.snackbar.open(message, 'Close', { duration: 5000 });
            this.pauseRecording();
            this.showRecordingPlanLimitReachedSubject.next(true);
          }
        })
      )
      .subscribe();
  }

  async getAudioInputDevices(): Promise<MediaDeviceInfo[]> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter(device => device.kind === 'audioinput');
  }

  async startRecording(deviceId: string) {
    this.audioChunks = [];
    const constraints = {
      audio: { deviceId: deviceId ? { exact: deviceId } : undefined },
    };

    await navigator.mediaDevices
      .getUserMedia(constraints)
      .then(stream => {
        this.mediaStream = stream;
        this.mediaRecorder = new MediaRecorder(stream);
        this.mediaRecorder.ondataavailable = async event => {
          this.audioChunks.push(event.data);
        };
        this.mediaRecorder.start();
        this.setRecordingStatus(true);
        this.startDurationTimer();
        // this.snackbar.open('Recording started. Reopen Speech To Notes on Clinical Notes tab.', 'Close', { duration: 5000 });
      })
      .catch(error => {
        console.error('Error accessing microphone:', error);
      });
  }

  pauseRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.pause();
      this.setPausedStatus(true);
      this.stopDurationTimer();
    }
  }

  resumeRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
      this.mediaRecorder.resume();
      this.setPausedStatus(false);
      this.setRecordingStatus(true);
      this.startDurationTimer();
    }
  }

  stopRecording(): Promise<Blob> {
    return new Promise(resolve => {
      if (this.mediaRecorder) {
        this.mediaRecorder.onstop = () => {
          const audioBlob = new Blob(this.audioChunks, { type: 'audio/wav' });
          this.stopMediaTracks();
          resolve(audioBlob);
          this.setRecordingStatus(false);
        };
        this.mediaRecorder.stop();
      }
    });
  }

  resetRecording() {
    if (this.mediaRecorder) {
      if (this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
      this.mediaRecorder = null as any;
    }
    this.audioChunks = [];
    this.setRecordingStatus(false);
    this.stopMediaTracks();
  }

  deleteAudio() {
    if (!this.componentVisibleSubject.getValue()) {
      return; // Don't delete if component isn't visible
    }
    this.resetRecording();
    this.audioChunks = [];
    this.setRecordingStatus(false);
    this.stopMediaTracks();
  }

  private stopMediaTracks() {
    if (!this.mediaStream) return;
    this.mediaStream.getTracks().forEach(track => track.stop());
    this.mediaStream = null as any;
  }

  private uploadToApi(audioData: Uint8Array, snapshot: RecordingSnapshot) {
    const fileName = `${snapshot.practiceId}_${snapshot.encounterId}.wav`;
    const fileToSend = new File([audioData], fileName, { type: 'audio/wav' });
    const contentType = fileToSend.type || 'application/octet-stream';

    this.noraAPIService
      .getPresignedUrl(
        snapshot.practiceId,
        snapshot.encounterId,
        fileName,
        contentType,
        snapshot.micUsed,
        snapshot.recordingDuration,
        snapshot.dictationMode,
        snapshot.language
      )
      .pipe(
        switchMap(response => {
          const presignedUrl = response.url;
          return from(
            fetch(presignedUrl, {
              method: 'PUT',
              headers: {
                'Content-Type': contentType,
              },
              body: fileToSend,
            })
          );
        }),
        tap(result => {
          if (result.status !== 200) {
            throw new Error(`Nora upload failed with status ${result.status}: ${result.statusText}`);
          }
        }),
        devLog('Successfully uploaded audio to Nora'),
        catchError(error => {
          logError('speech-to-notes: failed to upload audio to Nora', error);
          this.snackbar.open('Failed to upload the recording. Please try again.', 'Close', { duration: 5000 });
          return EMPTY;
        })
      )
      .subscribe();
  }

  private async handleAudioData(blob: Blob, snapshot: RecordingSnapshot) {
    const arrayBuffer = await this.blobToArrayBuffer(blob);
    const audioData = new Uint8Array(arrayBuffer);
    this.uploadToApi(audioData, snapshot);
  }

  private blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
    // Convert blob to array buffer
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = reject;
      reader.readAsArrayBuffer(blob);
    });
  }

  public notifyHandleAudioData(micUsed: string, recordingDuration: number, dictationMode: string, language: Language) {
    this.recordingDuration = recordingDuration;
    this.micUsed = micUsed;
    this.dictationMode = dictationMode;
    this.language = language;
    const snapshot: RecordingSnapshot = {
      practiceId: this.practiceId,
      encounterId: this.encounterId,
      micUsed,
      recordingDuration,
      dictationMode,
      language,
    };
    const audioBlob = new Blob(this.audioChunks, { type: 'audio/wav' });
    this.handleAudioDataSubject.next({ blob: audioBlob, snapshot });
    this.audioChunks = [];
    this.recordingSubmittedSubject.next();
  }

  getTenantEncounterIDs(): Observable<{ patientId: string; practiceTenantId: string; practiceId: string; encounterId: string }> {
    return this.encounter$.pipe(
      take(1), // Take only the first emission and complete
      withLatestFrom(this.params$), // Combine the latest value from params$
      map(([encounter, params]) => {
        // console.log('encounter', encounter);
        // console.log('params', params);
        // Transform the values to the desired data object
        // console.log(params.tenantId, encounter.EncounterId);
        return {
          patientId: params.patientId,
          practiceTenantId: encounter.PracticeId,
          practiceId: params.tenantId,
          encounterId: encounter.EncounterId,
        };
      })
    );
  }

  getPracticeId(): string | null {
    return this.practiceId;
  }

  getEncounterId(): string | null {
    return this.encounterId;
  }

  setPracticeId(practiceId: string | null): void {
    this.practiceId = practiceId;
  }

  setEncounterId(encounterId: string | null): void {
    this.encounterId = encounterId;
  }

  setPracticeTenantId(practiceTenantId: string | null): void {
    this.practiceTenantId = practiceTenantId;
  }

  getPracticeTenantId(): string | null {
    return this.practiceTenantId;
  }

  setRecordingStatus(isRecording: boolean): void {
    this.recordingSubject.next(isRecording);
  }

  setPausedStatus(isPaused: boolean): void {
    this.pausedSubject.next(isPaused);
  }

  getRecordingStatus(): boolean {
    return this.recordingSubject.getValue();
  }

  setProcessingNotesStatus(isProcessing: boolean): void {
    this.isProcessingNotesSubject.next(isProcessing);
  }

  getProcessingNotesStatus(): boolean {
    return this.isProcessingNotesSubject.getValue();
  }

  // Add method to update visibility
  setComponentVisibility(isVisible: boolean): void {
    this.componentVisibleSubject.next(isVisible);
  }

  setRecordingDuration(duration: number): void {
    this.recordingDurationSubject.next(duration);
  }

  setDurationInterval(interval: any): void {
    this.durationIntervalSubject.next(interval);
  }

  getRecordingDuration(): number {
    return this.recordingDurationSubject.getValue();
  }

  setPatientId(patientId: string): void {
    this.patientIdSubject.next(patientId);
  }

  getPatientId(): string {
    return this.patientIdSubject.getValue();
  }

  setStepperStage(stage: any): void {
    this.stepperStageSubject.next(stage);
  }

  getStepperStage(): any {
    return this.stepperStageSubject.getValue();
  }

  setGenerationProcessingStatus(isProcessing: boolean): void {
    this.generationProcessingSubject.next(isProcessing);
  }

  getGenerationProcessingStatus(): boolean {
    return this.generationProcessingSubject.getValue();
  }

  startDurationTimer() {
    this.stopDurationTimer();
    this.showRecordingPlanLimitReachedSubject.next(false);

    this.durationInterval = setInterval(() => {
      const currentDuration = this.recordingDurationSubject.getValue();
      this.recordingDurationSubject.next(currentDuration + 1);
    }, 1000);
  }

  stopDurationTimer() {
    if (this.durationInterval) {
      clearInterval(this.durationInterval);
      this.durationInterval = null;
    }
  }

  resetDuration() {
    this.stopDurationTimer();
    this.recordingDurationSubject.next(0);
    this.showRecordingPlanLimitReachedSubject.next(false);
  }

  setSelectedDevice(deviceId: string, deviceLabel: string) {
    this.selectedDeviceSubject.next({ id: deviceId, label: deviceLabel });
  }

  getSelectedDevice() {
    return this.selectedDeviceSubject.getValue();
  }

  setLanguage(language: Language): void {
    this.language = language;
  }

  getLanguage(): Language {
    return this.language;
  }

  concludeRecording$(): Observable<void> {
    return this.noraManagementService.getRecordingMode().pipe(
      take(1),
      switchMap(dictationMode => {
        const recordingDuration = this.getRecordingDuration();
        const micUsed = this.getSelectedDevice().label;
        return from(this.stopRecording()).pipe(
          tap(() => {
            this.notifyHandleAudioData(micUsed, recordingDuration, dictationMode, this.language);
            this.resetDuration();
          }),
          switchMap(() => this.pollForGeneratedNotes$(recordingDuration, micUsed))
        );
      })
    );
  }

  calculatePollTime(audioDuration: number = 0): number {
    const eightPercent = Math.ceil(audioDuration * 0.08 + 60);
    return Math.max(eightPercent, 26);
  }

  private captureConcludedEncounterContext$(): Observable<ConcludedEncounterContext | null> {
    const patientId = this.getPatientId();
    const practiceId = this.getPracticeId();
    return this.clinicalEncounterService.encounterInProgressOrNulll$(practiceId, patientId).pipe(
      take(1),
      map(encounter => {
        if (!encounter) {
          throw new Error('No in-progress encounter found for this patient');
        }
        return { encounter, practiceId, encounterId: encounter.EncounterId, practiceTenantId: encounter.PracticeId, patientId };
      }),
      catchError(() => {
        this.snackbar.open('The consultation for this patient has been concluded. Unable to upload audio.', 'Close', { duration: 5000 });
        return of(null);
      })
    );
  }

  applyGeneratedNote(progress: any, encounter: EncounterStatusVo): void {
    const content = quillDeltaToClinicalNoteHtml(progress.output);
    this.clinicalEncounterService.updateClinicalNote(encounter.PatientEventDetails.PracticeId, encounter.PatientId, content);
    this.clinicalEncounterService.updateSpeechToNotes(
      encounter.PracticeId,
      encounter.EncounterId,
      content,
      encounter.Provider.TreatingDoctorName,
      encounter.EncounterLineItems.EncounterHeader.DateOfService.toISOString() as unknown as Date
    );
  }

  pollForGeneratedNotes$(recordingDuration: number, micUsed: string): Observable<any> {
    return this.captureConcludedEncounterContext$().pipe(
      filter((context): context is ConcludedEncounterContext => !!context),
      switchMap(context => {
        const inProgressPoll$ = this.activePolls.get(context.encounterId);
        if (inProgressPoll$) {
          return inProgressPoll$;
        }

        let noteVersion = 1;
        let reachedTerminalStatus = false;
        const poll$ = interval(1000).pipe(
          delay(1000),
          take(this.calculatePollTime(recordingDuration)),
          exhaustMap(() =>
            from(this.noraAPIService.getConsultationStatus(context.practiceId, context.encounterId)).pipe(
              // Swallow a single failed tick so a network blip doesn't kill the whole
              // poll; the next interval tick retries within the existing time budget.
              catchError(error => {
                logError('speech-to-notes: poll tick for consultation status failed, retrying next tick', error);
                return EMPTY;
              })
            )
          ),
          tap(progress => {
            this.setStepperStage(progress.status);
            noteVersion = progress.version ? progress.version : 1;
            reachedTerminalStatus = progress.status === 'generated' || progress.status === 'error';
            if (progress.status === 'generated') {
              this.applyGeneratedNote(progress, context.encounter);
            }
          }),
          takeWhile(progress => progress.status !== 'generated' && progress.status !== 'error', true),
          // Structurally guaranteed reset: runs whether the loop ends via 'generated',
          // 'error', or take(N) exhausting the poll budget without either.
          finalize(() => {
            this.activePolls.delete(context.encounterId);
            this.setProcessingNotesStatus(false);
            this.setGenerationProcessingStatus(false);
            if (!reachedTerminalStatus) {
              this.snackbar.open('Still processing your notes — this is taking longer than usual. Please check back shortly.', 'Close', {
                duration: 8000,
              });
            }
            this.clinicalEncounterService.updateSpeechToNotesNoteVersion(
              context.practiceTenantId,
              context.encounterId,
              noteVersion,
              recordingDuration,
              micUsed
            );
          }),
          shareReplay(1)
        );

        this.activePolls.set(context.encounterId, poll$);
        return poll$;
      })
    );
  }

  confirmConcludeRecording$(dialog: MatDialog): Observable<ConcludeRecordingResult> {
    const recordingPatientId = this.getPatientId();
    if (!this.getRecordingStatus() || !recordingPatientId) {
      return of<ConcludeRecordingResult>('cancelled');
    }
    return this.uiStateService.activePatients$.pipe(
      take(1),
      switchMap(activePatients => {
        const recordingPatientName = activePatients.find(p => p.patientId === recordingPatientId)?.patientName || 'this patient';
        const dialogData: ConfirmDialogModel = {
          title: 'Recording in Progress',
          message: `You have a recording in progress for ${recordingPatientName}. Do you want to conclude this recording?`,
          actions: [defaultDialogActions.CANCEL, { key: DialogResponse.YES, value: 'Conclude Recording', color: 'primary' }],
        };
        const dialogClosed$ = dialog.open(ConfirmDialogComponent, { width: '450px', data: dialogData }).afterClosed();

        const cancelled$ = dialogClosed$.pipe(
          filter(result => !result || result.key !== DialogResponse.YES),
          map(() => 'cancelled' as ConcludeRecordingResult)
        );

        const concluded$ = dialogClosed$.pipe(
          filter(result => !!result && result.key === DialogResponse.YES),
          tap(() => this.concludeRequestedSubject.next()),
          map(() => 'concluded' as ConcludeRecordingResult)
        );

        return merge(cancelled$, concluded$);
      })
    );
  }

  recordingStatusFor(patientId: string): Observable<RecordingStatus> {
    if (!patientId) return of('idle');
    if (!this.statusCache.has(patientId)) {
      this.statusCache.set(
        patientId,
        combineLatest([this.recording$, this.paused$]).pipe(
          map(([rec, paused]) => {
            if (!rec || this.getPatientId() !== patientId) {
              return 'idle' as RecordingStatus;
            }
            return (paused ? 'paused' : 'recording') as RecordingStatus;
          }),
          distinctUntilChanged(),
          shareReplay({ bufferSize: 1, refCount: true })
        )
      );
    }
    return this.statusCache.get(patientId);
  }

  confirmIfAnyRecordingActive$(dialog: MatDialog): Observable<'proceed' | 'navigate_to_recording' | 'cancel'> {
    type Result = 'proceed' | 'navigate_to_recording' | 'cancel';
    if (!this.getRecordingStatus()) {
      return of<Result>('proceed');
    }
    const dialogData: ConfirmDialogModel = {
      title: 'Recording in Progress',
      message: 'There is an active Nora recording. Would you like to go back to the recording?',
      actions: [defaultDialogActions.CANCEL, { key: DialogResponse.YES, value: 'Go to Recording', color: 'primary' }],
    };
    return dialog
      .open(ConfirmDialogComponent, { width: '450px', data: dialogData })
      .afterClosed()
      .pipe(map(result => (!!result && result.key === DialogResponse.YES ? 'navigate_to_recording' : 'cancel') as Result));
  }

  guardedLogout$(dialog: MatDialog) {
    return this.confirmIfAnyRecordingActive$(dialog).pipe(
      withLatestFrom(this.authService.currentTenantId$),
      tap(([result, tenantId]) => {
        if (result === 'navigate_to_recording') {
          this.navService.navigate([tenantId, 'patient', this.getPatientId(), 'consult']);
          this.uiStateService.navigateSidebar(SidebarLocations.NORA_CONSULT_SUMMARY);
        }
      }),
      filter(([result]) => result === 'proceed'),
      switchMap(() => dialog.open(LogoutConfirmationDialogComponent, { width: '250px' }).afterClosed()),
      filter(confirmed => !!confirmed),
      tap(() => {
        this.authService.logout();
        this.navService.navigate(['/auth/login']);
      })
    );
  }

  confirmIfOtherPatientRecording$(
    dialog: MatDialog,
    currentPatientId$: Observable<string>
  ): Observable<'proceed' | 'navigated' | 'cancelled'> {
    type Result = 'proceed' | 'navigated' | 'cancelled';
    return currentPatientId$.pipe(
      take(1),
      switchMap(currentPatientId => {
        const recordingPatientId = this.getPatientId();
        if (!this.getRecordingStatus() || !recordingPatientId || recordingPatientId === currentPatientId) {
          return of<Result>('proceed');
        }
        const dialogData: ConfirmDialogModel = {
          title: 'Recording in Progress',
          message: 'There is an active Nora recording for another patient. Would you like to go back to it?',
          actions: [defaultDialogActions.CANCEL, { key: DialogResponse.YES, value: 'Go to Recording', color: 'primary' }],
        };
        return dialog
          .open(ConfirmDialogComponent, { width: '450px', data: dialogData })
          .afterClosed()
          .pipe(
            withLatestFrom(this.authService.currentTenantId$),
            map(([result, tenantId]) => {
              if (!!result && result.key === DialogResponse.YES) {
                this.navService.navigate([tenantId, 'patient', recordingPatientId, 'consult']);
                this.uiStateService.navigateSidebar(SidebarLocations.NORA_CONSULT_SUMMARY);
                return 'navigated' as Result;
              }
              return 'cancelled' as Result;
            })
          );
      })
    );
  }

  confirmClosePatientTab$(dialog: MatDialog, patientId: string): Observable<'close' | 'navigate_to_recording' | 'cancel'> {
    type Result = 'close' | 'navigate_to_recording' | 'cancel';
    return this.recordingStatusFor(patientId).pipe(
      take(1),
      switchMap(status => {
        if (status === 'idle') {
          return of<Result>('close');
        }
        const dialogData: ConfirmDialogModel = {
          title: 'Recording in Progress',
          message: 'This patient has an active Nora recording. Would you like to go back to the recording?',
          actions: [defaultDialogActions.CANCEL, { key: DialogResponse.YES, value: 'Go to Recording', color: 'primary' }],
        };
        return dialog
          .open(ConfirmDialogComponent, { width: '450px', data: dialogData })
          .afterClosed()
          .pipe(map(result => (!!result && result.key === DialogResponse.YES ? 'navigate_to_recording' : 'cancel') as Result));
      })
    );
  }

  ngOnDestroy(): void {
    // Only cleanup if we're actually stopping everything
    if (this.mediaRecorder && !this.recordingSubject.getValue()) {
      this.deleteAudio();
    }
  }
}
